import { Link } from 'react-router-dom';
import type { Flow } from '@/lib/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Star, MessageCircle, Zap, Play } from 'lucide-react';
import { getFlowIcon } from '@/lib/flowIconMapping.tsx';

// unused:
const renderStarRating = (rating: number) => {
  return (
    <div className="flex items-center space-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star key={star} className={`h-4 w-4 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
      ))}
    </div>
  );
};


export function FlowCard({ flow, onFlowClick }: { flow: Flow; onFlowClick: (flow: Flow) => void }) {
  const IconComponent = getFlowIcon(flow.icon as string);

  return (
    <Card
      key={flow.id}
      className="group relative overflow-hidden transition-all hover:shadow-xl cursor-pointer bg-white border border-gray-200 w-full min-w-[280px] sm:min-w-[320px] md:min-w-[360px] max-w-[400px] sm:w-auto sm:flex-1 sm:max-w-[400px] select-none"
      onClick={() => onFlowClick(flow)}>
      {flow.featured && (
        <div className="absolute right-2 top-2 z-10">
          <Badge variant="secondary" className="bg-accent text-accent-foreground">
            Featured
          </Badge>
        </div>
      )}

      <div className="relative h-48 overflow-hidden">
        <img
          src={flow.image || '/placeholder.svg'}
          alt={`${flow.name} preview`}
          className="h-full w-full object-cover transition-transform group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
      </div>

      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <IconComponent className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">
                <Link
                  to={`/gallery/flow/${flow.id}`}
                  className="hover:text-primary group-hover:text-green-600 hover:text-green-400 transition-all"
                  onClick={(e) => e.stopPropagation()}>
                  {flow.name}
                </Link>
              </CardTitle>
              <p className="text-sm text-muted-foreground">by {flow.author || 'Unknown Author'}</p>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pb-4">
        <CardDescription className="mb-4 line-clamp-2">{flow.description}</CardDescription>

        <div className="mb-3 flex flex-wrap gap-1">
          {flow.tags.map((tag) => (
            <Badge key={tag} variant="outline" className="text-xs">
              {tag}
            </Badge>
          ))}
        </div>

        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <span>{flow.rating}</span>
            </div>
            <Link
              to={`/gallery/flow/${flow.id}#comments`}
              className="flex items-center space-x-1 hover:text-green-600 transition-colors"
              onClick={(e) => e.stopPropagation()}>
              <MessageCircle className="h-4 w-4" />
              <span>{flow.comments?.length || 0} comments</span>
            </Link>
          </div>
          <span>{flow.downloads.toLocaleString()} downloads</span>
        </div>
      </CardContent>

      <CardFooter className="flex items-center justify-between pt-0">
        <div className="flex items-center space-x-1">
          <Zap className="h-4 w-4 text-green-700" />
          <span className="font-semibold text-green-700">{flow.credits} credits</span>
        </div>
        <Button
          size="sm"
          className="bg-green-600 hover:bg-green-700 transition-all text-white"
          onClick={(e) => {
            e.stopPropagation();
            // Handle add to workflow logic here
          }}>
          <Play className="h-4 w-4" />
          Add to Workflow
        </Button>
      </CardFooter>
    </Card>
  );
}
